/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import React from 'react';
import {
  Box,
  TextField,
  TextFieldProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { get } from 'react-hook-form';
import { sanitizeAutoComplete } from './sanitize-auto-complete';
import {
  VoiceRecordingButton,
  VoiceTranscriptOptions,
} from './voice-recording';

export type CustomTextFieldProps = Partial<TextFieldProps> & {
  errors?: Record<string, any>;
  helperText?: string;
  name: string;
};

export const CustomTextField = React.forwardRef<
  HTMLInputElement,
  CustomTextFieldProps
>(({ errors, name, helperText, ...rest }, ref) => {
  const error = get(errors, name);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  let label = rest.label || '';
  if (typeof label === 'string' && rest.required && label.match(/\s\*$/)) {
    label = label.replace(/\s\*$/, '');
  }

  const internalRef = React.useRef<HTMLInputElement | HTMLTextAreaElement>();
  const {
    inputRef,
    InputProps,
    inputProps,
    onChange,
    disabled,
    multiline,
    ...restWithoutInputRef
  } = rest;
  const readOnly = Boolean(InputProps?.readOnly || inputProps?.readOnly);
  const setInputRef = React.useCallback(
    (element: HTMLInputElement | HTMLTextAreaElement | null) => {
      internalRef.current = element ?? undefined;
      for (const targetRef of [inputRef, ref]) {
        if (typeof targetRef === 'function') targetRef(element);
        else if (targetRef) {
          (
            targetRef as React.MutableRefObject<
              HTMLInputElement | HTMLTextAreaElement | null
            >
          ).current = element;
        }
      }
    },
    [inputRef, ref],
  );
  const autoComplete = sanitizeAutoComplete(restWithoutInputRef.autoComplete);
  const handleTranscript = React.useCallback(
    (transcript: string, options: VoiceTranscriptOptions) => {
      const input = internalRef.current;
      if (!input || disabled || readOnly || input.readOnly) return;
      const currentValue = input.value.trimEnd();
      input.value = `${currentValue}${currentValue ? '\n' : ''}${transcript}`;
      onChange?.({
        target: input,
        currentTarget: input,
      } as React.ChangeEvent<HTMLInputElement>);
      if (options.focusInput) input.focus();
    },
    [disabled, onChange, readOnly],
  );

  const textField = (
    <TextField
      error={Boolean(error) || errors?.isError}
      helperText={error?.message || helperText}
      fullWidth
      variant="outlined"
      name={name}
      {...restWithoutInputRef}
      disabled={disabled}
      multiline={multiline}
      onChange={onChange}
      autoComplete={autoComplete}
      label={label}
      inputRef={setInputRef}
      inputProps={inputProps}
      InputProps={
        multiline
          ? {
              ...InputProps,
              endAdornment: (
                <>
                  {InputProps?.endAdornment}
                  {!isMobile && (
                    <Box sx={{ alignSelf: 'flex-end', mb: 0.75, ml: 1 }}>
                      <VoiceRecordingButton
                        disabled={Boolean(disabled || readOnly)}
                        onTranscript={handleTranscript}
                      />
                    </Box>
                  )}
                </>
              ),
            }
          : InputProps
      }
    />
  );
  if (!multiline || !isMobile) return textField;
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.75 }}>
        <VoiceRecordingButton
          disabled={Boolean(disabled || readOnly)}
          onTranscript={handleTranscript}
        />
      </Box>
      {textField}
    </Box>
  );
});
CustomTextField.displayName = 'CustomTextField';
